import React, { useState } from 'react';
import { Tag, Input, Select, Row, Col } from 'antd';
import { EyeOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useStores } from '../../hooks/useStores';
import { DataTable } from '../../components';
import type { Store } from '../../services/store.service';

const STORE_TYPE_LABEL: Record<number, string> = { 1: 'Tiêu chuẩn', 2: 'Chỉ bạc' };

const StoreList: React.FC = () => {
  const navigate = useNavigate();
  const [searchText, setSearchText] = useState('');
  const [stateFilter, setStateFilter] = useState<number | undefined>();
  const [typeFilter, setTypeFilter] = useState<number | undefined>();
  const [tenantFilter, setTenantFilter] = useState<string>('');

  const { stores, loading, meta, setFilters, refetch, filters } = useStores();

  const applyFilters = (overrides: Partial<typeof filters> = {}) => {
    setFilters({
      ...filters,
      page: 1,
      q: searchText || undefined,
      state: stateFilter,
      store_type: typeFilter,
      tenant_id: tenantFilter || undefined,
      ...overrides,
    });
  };

  const handleSearch = (value: string) => {
    setSearchText(value);
    applyFilters({ q: value || undefined });
  };

  const handlePageChange = (page: number, pageSize: number) => {
    setFilters({ ...filters, page, limit: pageSize });
  };

  const columns: ColumnsType<Store> = [
    {
      title: 'Mã / Tên',
      dataIndex: 'name',
      key: 'name',
      render: (name, record) => (
        <div>
          <a onClick={() => navigate(`/stores/${record.store_id}`)}>{name}</a>
          <div style={{ fontSize: 12, color: '#8c8c8c' }}>{record.code}</div>
        </div>
      ),
    },
    { title: 'Địa chỉ', dataIndex: 'address', key: 'address', ellipsis: true },
    { title: 'Hotline', dataIndex: 'hotline', key: 'hotline', width: 140 },
    {
      title: 'Loại',
      dataIndex: 'store_type',
      key: 'store_type',
      width: 110,
      render: (t: number) => <Tag>{STORE_TYPE_LABEL[t] || t}</Tag>,
    },
    {
      title: 'Tenant',
      dataIndex: 'tenant_id',
      key: 'tenant_id',
      width: 200,
      render: (id?: string) => id ? <code style={{ fontSize: 11 }}>{id.slice(-8)}</code> : '—',
    },
    {
      title: 'Trạng thái',
      dataIndex: 'state',
      key: 'state',
      width: 110,
      render: (s: number) => <Tag color={s === 1 ? 'success' : 'default'}>{s === 1 ? 'Hoạt động' : 'Tắt'}</Tag>,
    },
    {
      title: 'Ngày tạo',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 130,
      render: (date: string) => date ? dayjs(date).format('DD/MM/YYYY') : '—',
    },
  ];

  const filterContent = (
    <Row gutter={16}>
      <Col span={8}>
        <Input.Search
          placeholder="Tenant ID (tùy chọn)"
          allowClear
          value={tenantFilter}
          onChange={(e) => setTenantFilter(e.target.value)}
          onSearch={() => applyFilters()}
        />
      </Col>
      <Col span={6}>
        <Select
          placeholder="Trạng thái"
          allowClear
          style={{ width: '100%' }}
          value={stateFilter}
          onChange={(v) => { setStateFilter(v); applyFilters({ state: v }); }}
          options={[
            { value: 1, label: 'Hoạt động' },
            { value: 0, label: 'Tắt' },
          ]}
        />
      </Col>
      <Col span={6}>
        <Select
          placeholder="Loại"
          allowClear
          style={{ width: '100%' }}
          value={typeFilter}
          onChange={(v) => { setTypeFilter(v); applyFilters({ store_type: v }); }}
          options={[
            { value: 1, label: 'Tiêu chuẩn' },
            { value: 2, label: 'Chỉ bạc' },
          ]}
        />
      </Col>
    </Row>
  );

  return (
    <DataTable<Store>
      title="Cửa hàng (cross-tenant)"
      data={stores}
      columns={columns}
      loading={loading}
      rowKey="store_id"
      searchable
      searchPlaceholder="Tên / mã / địa chỉ..."
      onSearch={handleSearch}
      onRefresh={refetch}
      filters={filterContent}
      pagination={{
        current: meta.page,
        pageSize: meta.limit,
        total: meta.total,
        onChange: handlePageChange,
      }}
      onRowClick={(record) => navigate(`/stores/${record.store_id}`)}
      actionColumn={{
        width: 80,
        fixed: 'right',
        actions: (record) => [
          {
            key: 'view',
            label: 'Xem chi tiết',
            icon: <EyeOutlined />,
            onClick: () => navigate(`/stores/${record.store_id}`),
          },
        ],
      }}
    />
  );
};

export default StoreList;
export { StoreList };
